import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import CustomScroll from "react-custom-scroll";
import { useParams } from "react-router-dom";

import ListItem from "./listItem/ListItem";
import parseTimeString from "../../../hooks/parseTimeString";
import {getStoryList, updateStoryListBySort} from "../../../store/slices/storylist";
import {formatInterval} from "../../../hooks/formatInterval";
import {getColumns} from "../../admin/helper/multiselectColumns";
import { IDashboardConfig, IListData, IObjectKeys, IChannelData } from "../../../constants/interfaces";
import {ensureType} from "../../../hooks/ensureType";
import formatPostedOnChannels from "../../../hooks/formatPostedOnChannels";
import { getLineWidth, getLineSum } from "../../admin/helper/lineDistribution";
import { dashboardArrowDown, dashboardArrowUp } from "../../admin/icons";

const MAX_LEGEND_ITEMS = 7;

type Props = {
    cellId: number,
    variant: string
}

const ListWidget = (props: Props) => {
	const data = useSelector((state: {storylist: {data: IListData[]}}) => state.storylist.data[props.cellId]);
	const isLoading = useSelector((state: {storylist: {isLoading: boolean}}) => state.storylist.isLoading);
	const isUpdating = useSelector((state: {storylist: {isUpdating: number}}) => state.storylist.isUpdating);
	const dispatch = useDispatch();
	const widgetConfig = useSelector(
		(state: {dashboard: {data: IDashboardConfig}}) => ensureType(state.dashboard.data.grid_config.find(cell => cell.cell_id === props.cellId))
	);
	const [sortingField, setSortingField] = useState("");
	const [sortOrder, setSortOrder] = useState("ASC");
	const [fields, numberFields, eventFields, customFields, customFieldNames] = useMemo(() => {
		let fields = widgetConfig.fields;

		if (widgetConfig.custom_fields && widgetConfig.custom_fields.length > 0) {
			const customFieldNames = widgetConfig.custom_fields.map((customMetric: any) => customMetric.id);
			fields = fields.concat(customFieldNames);
		}

		let numberFields = fields.filter(field => !['title', 'section', 'brandName', 'relativeChannels'].includes(field));
		numberFields = numberFields.filter(numberField => getColumns("list").find(item => item.name === numberField)); // Remove old/unknown fields

		let eventFields = '';
		let customFields: Array<any> = [];
		let customFieldNames: Array<string> = [];

		if (widgetConfig.event_fields && widgetConfig.event_fields.length > 0) {
			numberFields = numberFields.concat(widgetConfig.event_fields.map(eventField => eventField.name));
			eventFields = widgetConfig.event_fields.map(eventField => eventField.name).join('$$');
		}


		if (widgetConfig.custom_fields && widgetConfig.custom_fields.length > 0) {
			customFieldNames = widgetConfig.custom_fields.map((customMetric: any) => customMetric.id);
			numberFields = numberFields.concat(customFieldNames);
			customFields = widgetConfig.custom_fields;
		}

		return [fields, numberFields, eventFields, customFields, customFieldNames];
	}, [widgetConfig]);
	const { variant } = props;
	const refreshRate = parseTimeString(widgetConfig.refresh);
	const brandName = fields.find(field => field === 'brandName');
	const section = fields.find(field => field === 'section');
	const brands = widgetConfig.brands.join(',');
	const title = widgetConfig.label;
	const timeWindow = widgetConfig.time_window.replace(/360m/,"6h").replace(/720m/,"12h").replace(/1440m/,"24h")
	const extraInfo = (widgetConfig.time_window === "midnight") ? "Today" : "Last " + timeWindow;
	const apiInterval = formatInterval(widgetConfig.time_window);
	const apiPibDateInterval = widgetConfig.pubdate_interval;
	const showNotifications = widgetConfig.show_circles !== 0;
	const notOnHomepage = widgetConfig.not_on_homepage !== 0;
	const isPaid = widgetConfig.paid_type;
	const videoFilter = widgetConfig.video_filter;
	const pageType = widgetConfig.page_type;
	const weightedAverage = widgetConfig.weighted_result !== 0;
	const sections = widgetConfig.sections.join('$$');
	const referrers = widgetConfig.referrers ? widgetConfig.referrers.join('$$') : '';
	const userNeeds = widgetConfig.user_needs ? widgetConfig.user_needs.join('$$') : '';
	const referrerTypes = widgetConfig.referrer_types ? widgetConfig.referrer_types.join('$$') : '';
	const tags = widgetConfig.tags ? widgetConfig.tags.join('$$') : '';
	const conversions = widgetConfig.conversions ? widgetConfig.conversions.join('$$') : '';
	const { includeChannels, excludeChannels } = formatPostedOnChannels(widgetConfig.posted_on_channels);
	const { id } = useParams() as any;

	const isCurrent = useMemo(()=>{
		if (id.toLowerCase().includes("current") || id.toLowerCase().includes("readingtime")) {
			return true;
		} return false;
	},[id])


	// initial first load
	useEffect(() => {
		let sort = "";
		if (sortingField && sortOrder) {
			sort = `${sortingField}.${sortOrder.toLowerCase()}`;
		} else {
			sort = widgetConfig.sort;
		}

		dispatch(getStoryList(brands, fields, apiInterval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, sort, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPibDateInterval, userNeeds, customFieldNames, props.cellId));
	}, [dispatch, widgetConfig, brands, fields, apiInterval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPibDateInterval, userNeeds, customFieldNames, props.cellId]);



	// update data
	useEffect(() => {
		let sort = "";
		if (sortingField && sortOrder) {
			sort = `${sortingField}.${sortOrder.toLowerCase()}`;
		} else {
			sort = widgetConfig.sort;
		}

		const interval = setInterval(() => {
			dispatch(getStoryList(brands, fields, apiInterval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, widgetConfig.sort, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPibDateInterval, userNeeds, customFieldNames, props.cellId));
		}, refreshRate);
		return () => clearInterval(interval);
	}, [dispatch, refreshRate, widgetConfig, brands, fields, apiInterval, showNotifications, notOnHomepage, eventFields, sections, tags, conversions, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPibDateInterval, userNeeds, customFieldNames, props.cellId]);


    // get relevant data from state array
    const listData = data?.filter((item) =>
        numberFields.some((field) => item[field] || item[field] === 0)
    );

	const isLoaded = !!(listData && listData.length);
	if (isLoading && !isLoaded) return <div>Loading...</div>;
	if (!listData) return <div>Loading...</div>;
	// gather all channel types
	const storyChannels = listData.map((p) => p.relativeChannels);
	const allChannels = storyChannels.reduce(
		(p: Array<IChannelData>, n) => p.concat(n.filter((c) => (c.total || 0) > 0)),
		[]
	);
	const channelLabels = Array.from(
		new Set(allChannels.map((c) => c.label)).values()
	);
	const channelTypes = channelLabels.map((label) => {
		const { color } = ensureType(allChannels.find((e) => e.label === label));
		return { label, color };
	}, {});
	const channelMaxes: IObjectKeys = channelLabels.reduce((store: IObjectKeys, label) => {
		store[label] = Math.max(...allChannels.filter(c => c.label === label).map(c => c.total));
		return store;
	}, {});
	const legendChannels = channelLabels.sort((a, b) => channelMaxes[b] - channelMaxes[a]).slice(0, MAX_LEGEND_ITEMS);
	// List item lines
	const totals = listData.map(item => item.totalPageviews || 0);
	const maxReach = totals.length > 0  ? totals.reduce((a, b) => Math.max(a, b)) : 0;


	const getFieldTitle = (field: string) => {
		const eventField = widgetConfig.event_fields ? widgetConfig.event_fields.find(eventField => eventField.name === field) : undefined;
		if (eventField) {
			return eventField.short_label;
		}

		const customMetric = widgetConfig.custom_fields && widgetConfig.custom_fields.length > 0 ? widgetConfig.custom_fields.find((customMetric: any) => customMetric.id === field) : undefined;
		if (customMetric) {
			return customMetric.label;
		}

		return getColumns("list").find(item => item.name === field).title;
	}

	const handleSort = (field : string) => {
		let sortingOrder = ""
		if (sortingField === field || widgetConfig.sort.split(".")[0] === field) {
			if (sortOrder === "DESC") {
				setSortOrder("ASC");
				sortingOrder = "ASC";
			} else {
				setSortOrder("DESC");
				sortingOrder = "DESC";
			}
			setSortingField(field);
		} else {
			setSortOrder("DESC");
			sortingOrder = "DESC";
			setSortingField(field);
		}


		const sort = `${field}.${sortingOrder.toLowerCase()}`;
		dispatch(updateStoryListBySort(brands, fields, apiInterval, showNotifications, notOnHomepage, sections, tags, conversions, eventFields, sort, weightedAverage, isPaid, videoFilter, pageType, referrers, referrerTypes, includeChannels, excludeChannels, apiPibDateInterval, userNeeds, customFieldNames, props.cellId));
	}

	return (
		<div
			className={`list-widget ${
				listData.length > 10 ? "scroll-enabled" : ""
			}`}
		>
			<div className="list-widget__heading">
				<div className="list-widget__title">{title}</div>

				<div className="list-widget__heading-right">
					<div style={{flex:1}}/>
					<ul className="legend">
						{channelTypes.filter(t => legendChannels.includes(t.label)).map((t) => (
							<li
								key={t.label}
								className="legend__item"
								style={{ color: t.color }}
							>
								<span>{t.label}</span>
							</li>
						))}
					</ul>
					{/*{variant === 'full' ? setBrand() : null}*/}
				</div>
			</div>
			<span className="list-widget__extra-info">{extraInfo}</span>
			<div className="list-widget__list">
				<div className="list-widget__fields">
					<div className="list-widget__heading-header" />
					<div className={`list-widget__heading-headers dynamic-flex-${
						numberFields.length + Number(variant === 'full' && !!section) + (isNaN(Number(brandName))? 0 : 1)
						}`} >
						{numberFields.map((field, index) => (
							<div
								className={`list-widget-stat-heading ${!isCurrent && "sortable"} ${field === "pubdate" ? "date" : ""} ${sortingField === field ? "sorted" : ""}`}
								key={index}
								onClick={()=>{
									if (!isCurrent) {
										handleSort(field)
									}
								}}
							>
								{
									sortingField === field ? 
										sortOrder === "ASC" ? dashboardArrowUp : dashboardArrowDown : ""
								}
								{" "}
								{getFieldTitle(field)}
								
								</div>
							))
					}
					{!!section && (
						<div className="list-widget-stat-heading list-widget__heading-extra">Section</div>
					)}
					{!!brandName && (
						<div className="list-widget-stat-heading list-widget-stat-heading-brand-name">
							Brand
						</div>
					)}
					</div>
				</div>
				<CustomScroll heightRelativeToParent="calc(100% - 80px)">
					{
						isUpdating  === props.cellId ? <span className="loading-sign">
						<span className="loading-sign__spinner" />
						</span> : listData.length > 0 ? listData.map((data) => {
							return (
								<ListItem
									key={`${data.relationId}_${data.brandId}`}
									data={data}
									lineWidth={getLineWidth(data.totalPageviews, maxReach)}
									lineSum={getLineSum(data.relativeChannels)}
									variant={variant}
									numberFields={numberFields}
									customFields={customFields}
									showBrand={!!brandName}
									showSection={!!section}
									showNotifications={showNotifications}
								/>
							);
						}) : <span>No data</span>
					}
					{/* {
						
					} */}
				</CustomScroll>
			</div>
		</div>
	);
};

export default ListWidget;
